/**@module csi/dndHTML5 */
define([
	"dojo/_base/event",
	"dojo/dom-class",
	"dojo/on"
], function(event, domClass, on) {

	/**
	 * This module should only be used in home.js or similar. This module is not supposed to be used in widgets.
	 * There might be only one instance of this module, it should be kept singleton
	 * 
	 * How to use: 
	 * Add this module to the list of required modules on home.js and call dndHTML5.init() once  
	 * 
	 * Example: home.js
	 * 
	 * NOTE: For widgets use csi/_DndHTML5Mixin module instead
	 * 
	 */

	/** @property {integer} _dndCounter - static, used internally for drag and drop 
	 * DragEnter and DragLeave being called on every child element's enter/leave, and 
	 * while entering child it is not calling parent’s leave, so we need to maintain count
	 * to catch the moment when it leaves the page and we have to end dragging effects */
	let _dndCounter = 0;

	/** @property {boolean} _bOwnDrag - indicates that drag is active and started on our page 
	 * Access function - dndHTML5.isOwnDrag()
	 * Used in csi/widget/_DndHTML5Mixin */
	let _bOwnDrag = false;

	/** @property {integer} _dialogLevel - tracks current dialog level */
	let _dialogLevel;

	/** @property {RemoveEventHandle[]} _eventHandles - event handles for events registered on body node */
	let _eventHandles;

	/**
	 * Prevent event propagation
	 * @property {DragEvent} ev
	 */
	function stopEvent(ev) {
		// Stop propagation (bubbling)
		event.stop(ev);
		// Prevent other handlers on the same element
		if (ev.stopImmediatePropagation) {
			ev.stopImmediatePropagation();
		}
	}

	/**
	 * ondragover event handler
	 * Set drop effect no "none", which indicate "no available drop".
	 * When develop DnD for particular widget/element, define your own handler and set drop effect to "copy" (our default), "move" or "link".  
	 * Example: ImageReplace dialog, Preferences, Gallery, Table, ImageBrowser, Canvas  
	 * @property {DragEvent} ev
	 */
	function doDragOverNone(ev) {
		stopEvent(ev);

		var dataTransfer = ev.dataTransfer;
		dataTransfer.dropEffect = 'none';
	}

	/**
	 * ondrop event handler
	 * Prevent web page redirection when user drop something on the application body
	 * When develop DnD for particular widget/element, define your own doDrop handler and process is as required.
	 * Example: ImageReplace dialog, Preferences, Gallery, Table, ImageBrowser, Canvas  
	 * Note: you don't need to set _dndCounter = 0 on your handler
	 * @property {DragEvent} ev
	 */
	function doNoDrop(ev) {
		stopEvent(ev);
		_dndCounter = 0;
		setDragActive(false);
	}

	/**
	 * ondragenter event handler
	 * Enable "Drag Active" state and increase _dndCounter  
	 * @property {DragEvent} ev
	 */
	function doDragEnter(ev) {
		stopEvent(ev);
		if (!_bOwnDrag) {
			setDragActive(true);
		}
		_dndCounter++; // Counter should be increased after the call to setDragActive(true)  
	}

	/**
	 * ondragleave event handler
	 * Decrease_dndCounter and remove "Drag Active" state when counter comes to 0   
	 * @property {DragEvent} ev
	 */
	function doDragLeave(ev) {
		stopEvent(ev);
		_dndCounter--;  // Counter should be decreased before the call to setDragActive(false)
		setDragActive(false);
	}

	/**
	 * Track drags started on our page
	 * @property {DragEvent} ev
	 */
	function doDragStart(ev) {
		_bOwnDrag = true;
	}

	/**
	 * Stop tracking drag started on our page
	 * @property {DragEvent} ev
	 */
	function doDragEnd(ev) {
		_bOwnDrag = false;
	}

	/**
	 * Return true if drag started on our page
	 * @return {boolean}
	 */
	function isOwnDrag() {
		return _bOwnDrag;
	}

	/**
	 * Set "csi-drag" class on body. Developers supposed to use this class when developing their widgets to indicate drop position
	 * @param {bActive} boolean
	 */
	function setDragActive(bActive) {
		if (_dndCounter == 0) {
			domClass.toggle(document.body, "csi-drag", bActive);
		}
	}

	/**
	 * Getter for dialog level
	 * @return {number}
	 */
	function getDialogLevel() {
		return _dialogLevel;
	}

	/**
	 * Setter for dialog level
	 * @param {integer} level - set dialog level
	 */
	function setDialogLevel(level) {
		_dialogLevel = level;
	}

	/**
	 * paste event handler
	 * Dummy for this module, added for documentation purpose only
	 * Developers should connect this event to their doDrop handler (they are compatible) or introduce their own doPaste handler if needed
	 * Developer can catch this event from their widget or entire body as needed 
	 * NOTE: You don't need this event if you use _DndHTML5Mixin
	 * Example: ImageReplace dialog, Preferences, Gallery, Table, ImageBrowser, Canvas  
	 * Note: Do not uncomment this event here on body, as it will intersect all paste attempts to all controls therefore fully disable paste in entire application
	 */
	//	function doPaste(ev){
	//	}

	/** initialize drag/drop events */
	function init() {
		const {body} = document;

		_eventHandles = _eventHandles || [
			on(body, "drop", doNoDrop),
			on(body, "dragover", doDragOverNone),
			on(body, "dragenter", doDragEnter),
			on(body, "dragleave", doDragLeave),
			on(body, "dragstart", doDragStart),
			on(body, "dragend", doDragEnd),
			// on(body, "paste", doPaste), // added for documentation purpose
		];
	}

	/** remove drag/drop events registered in init */
	function reset() {
		if (_eventHandles) {
			for (const h of _eventHandles) {
				h.remove();
			}

			_eventHandles = null;
		}
	}

	return {
		getDialogLevel,
		init,
		isOwnDrag,
		reset,
		setDialogLevel,
		stopEvent,
	};
});